import React, { useContext, useMemo, useState } from "react";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { UsersEntity } from "entities/Users/UsersEntity";
import { IGetUserFiltersPreferencesQuery } from "api/queries/usersAPI";
import { FiltersPreferencesReportsEnum } from "constants/constants";
import { Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { filterObjectProperties, objectComparator } from "utilities/utilities";
import { IRCAActionPlanFiltersContextProps } from "./RCAActionPlanFiltersContextInterfaces";
import { initialRCAActionPlanFilters, RCAActionPlanFiltersContext } from "./RCAActionPlanFiltersContext";
import { IRCAActionPlansFilterSelected } from "pages/Corporate/RCA/RCAActionPlans/components/RCAActionPlansFilters/RCAActionPlansFiltersInterfaces";

export const valiRCAActionPlanFiltersDefaultFields = [
    "showCompleted",
    "meetingTypeIds",
    "totalMeetingTypes",
    "ownerIds",
    "statusesIds",
    "searchText",
];

export const RCAActionPlanFiltersContextProvider: React.FC<IRCAActionPlanFiltersContextProps> = ({ children }) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    const [filters, setFilters] = useState(initialRCAActionPlanFilters);
    const [userFiltersDefault, setUserFiltersDefault] = useState<IRCAActionPlansFilterSelected | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { upsertOperation, loading: loadingUpsertDefault } = UsersEntity.UpsertUserFiltersDefaultPreference();
    const { removeOperation, loading: loadingRemoveDefault } = UsersEntity.RemoveUserFiltersDefaultPreference();

    const handleQueryCompletion = (data: IGetUserFiltersPreferencesQuery) => {
        const userFilterPreference = data.user_filter_preferences;
        if (userFilterPreference[0]) {
            const filtersDefault = JSON.parse(
                userFilterPreference[0].filter_selection,
            ) as IRCAActionPlansFilterSelected;
            setUserFiltersDefault(filtersDefault);
            setFilters((prev) => ({ ...prev, ...filtersDefault }));
        } else {
            setUserFiltersDefault(null);
        }
    };
    const { loading: loadingGetDefault, refetch } = UsersEntity.GetUserFiltersPreferences(
        handleQueryCompletion,
        FiltersPreferencesReportsEnum.RCA_ACTION_PLANS,
        currentUser?.user_id,
    );

    const handleFiltersChange = (value: IRCAActionPlansFilterSelected) => {
        setFilters(value);
    };

    const handleSetFiltersDefault = async (filters: IRCAActionPlansFilterSelected) => {
        try {
            const validFilters = filterObjectProperties(filters, valiRCAActionPlanFiltersDefaultFields);
            await upsertOperation({
                variables: {
                    userPreference: {
                        user_id: currentUser?.user_id,
                        report_id: FiltersPreferencesReportsEnum.RCA_ACTION_PLANS,
                        filter_selection: JSON.stringify(validFilters),
                    },
                },
            });
            await refetch();
            setSuccessMessage(t("locations.mill_dashboards.filters_saved"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleRemoveDefaultFilters = async () => {
        try {
            await removeOperation({
                variables: {
                    userId: currentUser?.user_id,
                    reportId: FiltersPreferencesReportsEnum.RCA_ACTION_PLANS,
                },
            });
            setUserFiltersDefault(null);
            setSuccessMessage(t("locations.mill_dashboards.filters_removed"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const isFiltersDefault = useMemo(() => {
        if (userFiltersDefault) {
            return objectComparator(userFiltersDefault, filters, valiRCAActionPlanFiltersDefaultFields);
        }

        return false;
    }, [userFiltersDefault, filters]);

    const providerValue = {
        filters,
        handleFiltersChange,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        userFiltersDefault,
        loadingDefaultOperations: loadingGetDefault || loadingRemoveDefault || loadingUpsertDefault,
        isFiltersDefault,
    };

    return (
        <RCAActionPlanFiltersContext.Provider value={providerValue}>
            <Snackbar
                open={!!successMessage || !!errorMessage}
                autoHideDuration={2000}
                onClose={() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={successMessage ? "success" : "error"} sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{successMessage || errorMessage}</span>
                </Alert>
            </Snackbar>
            {children}
        </RCAActionPlanFiltersContext.Provider>
    );
};
